const libPictProvider = require('pict').ServiceProviderBase;

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'SessionParser',
	Dependencies: []
};

/*
	Pict-Provider-SessionParser
	---------------------------
	Pure transform: raw JSONL records (and optional sidecar files) → a
	normalized Session model placed at AppData.Session.

	Inputs:
		.parseJSONLText(pText, pSidecars)      // single .jsonl string + optional sidecars map
		.parseZip(pArrayBuffer)                // export .zip arraybuffer (uses JSZip in browser)
		.parseFiles(pFileList)                 // browser FileList (jsonl, zip, or expanded export folder)

	The "Session" model written to AppData.Session has shape:
		{
			Meta:    { sessionId, title, model, cwd, createdAt, lastActivityAt, version, ... },
			Turns:   [ Turn ... ]                  // chronological top-level events for the main thread
			Prompts: [ PromptGroup ... ]           // grouped by promptId
			Agents:  { <agentId>: AgentTranscript }
			Stats:   { totalDurationMs, idleMs, activeMs, toolCallCount, promptCount, agentCount, longestGapMs }
		}

	A "Turn" is a normalized item ready for the conversation view:
		{
			Kind:        'user-prompt' | 'assistant' | 'tool-result' | 'attachment' | 'system' | 'gap' | 'title',
			Uuid, ParentUuid, PromptId, Timestamp (ISO), TimestampMs,
			DurationFromPrevMs,                    // gap from previous Turn (used to render "idle 5h")
			Blocks:      [ Block ... ],            // text/thinking/tool_use/tool_result/image/etc.
			ToolUseIds:  [ ... ],                  // ids of tool_use blocks emitted in this turn
			Raw:         <original record>,        // kept for drill-in/debug; trimmed of huge content
		}

	A "PromptGroup" wraps everything caused by one user prompt:
		{
			PromptId, Title, FirstTimestampMs, LastTimestampMs, ResponseLatencyMs,
			TotalDurationMs, IdleAfterMs, ToolCallCount, AgentCallCount,
			TurnIndices: [...],                    // indices into Session.Turns
		}
*/

const _MaxInlineToolResultPreview = 8000;          // chars kept inline before we offer "open full"
const _MinIdleGapMs = 60 * 1000;                   // visual gap threshold: shorter pauses don't render in the timeline
const _StandbyThresholdMs = 5 * 60 * 1000;         // stats threshold: gaps under this count as "you working" (typing/reading), over count as standby (AFK)

class PictProviderSessionParser extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);

		this._JSZip = null;                           // lazy: only needed for zip parsing
	}

	// ---------------------------------------------------------------------
	// Top-level entry points
	// ---------------------------------------------------------------------

	parseFiles(pFileList, fCallback)
	{
		let tmpFiles = Array.from(pFileList || []);
		if (tmpFiles.length === 0)
		{
			return fCallback(new Error('No files supplied.'));
		}

		// Single zip file?
		if (tmpFiles.length === 1 && /\.zip$/i.test(tmpFiles[0].name))
		{
			return this._readFileAsArrayBuffer(tmpFiles[0], (pErr, pBuf) =>
			{
				if (pErr) return fCallback(pErr);
				return this.parseZip(pBuf, fCallback);
			});
		}

		// Single jsonl file?
		if (tmpFiles.length === 1 && /\.jsonl$/i.test(tmpFiles[0].name))
		{
			return this._readFileAsText(tmpFiles[0], (pErr, pText) =>
			{
				if (pErr) return fCallback(pErr);
				return this.parseJSONLText(pText, {}, fCallback);
			});
		}

		// A whole expanded export folder dragged in. Find the .jsonl and gather the
		// tool-results/ + subagents/ siblings by relative path.
		return this._parseDroppedFolder(tmpFiles, fCallback);
	}

	parseZip(pArrayBuffer, fCallback)
	{
		let tmpJSZip = this._loadJSZip();
		if (!tmpJSZip)
		{
			return fCallback(new Error('JSZip not available in this environment.'));
		}

		tmpJSZip.loadAsync(pArrayBuffer).then((pZip) =>
		{
			let tmpJsonlEntry = null;
			let tmpToolResults = {};                         // key (filename without dir) → string
			let tmpSubagents = {};                            // agentId → { Meta, Lines: [...] }
			let tmpMetadata = null;
			let tmpReads = [];

			pZip.forEach((pPath, pEntry) =>
			{
				if (pEntry.dir) return;
				if (/\.jsonl$/i.test(pPath) && pPath.indexOf('/subagents/') === -1)
				{
					tmpJsonlEntry = pEntry;
					return;
				}
				if (/\/metadata\.json$/i.test(pPath))
				{
					tmpReads.push(pEntry.async('string').then((pText) => { try { tmpMetadata = JSON.parse(pText); } catch(e){} }));
					return;
				}
				if (pPath.indexOf('/tool-results/') !== -1 && /\.txt$/i.test(pPath))
				{
					let tmpKey = pPath.split('/').pop();
					tmpReads.push(pEntry.async('string').then((pText) => { tmpToolResults[tmpKey] = pText; }));
					return;
				}
				let tmpAgentMatch = pPath.match(/\/subagents\/agent-([^./]+)(\.meta\.json|\.jsonl)$/i);
				if (tmpAgentMatch)
				{
					let tmpAgentId = tmpAgentMatch[1];
					let tmpKind = tmpAgentMatch[2];
					tmpSubagents[tmpAgentId] = tmpSubagents[tmpAgentId] || { Meta: null, Text: '' };
					if (tmpKind === '.meta.json')
					{
						tmpReads.push(pEntry.async('string').then((pText) => { try { tmpSubagents[tmpAgentId].Meta = JSON.parse(pText); } catch(e){} }));
					}
					else
					{
						tmpReads.push(pEntry.async('string').then((pText) => { tmpSubagents[tmpAgentId].Text = pText; }));
					}
				}
			});

			if (!tmpJsonlEntry)
			{
				return fCallback(new Error('No .jsonl found in zip.'));
			}

			tmpReads.push(tmpJsonlEntry.async('string').then((pText) =>
			{
				let tmpSidecars = { ToolResults: tmpToolResults, Subagents: tmpSubagents, Metadata: tmpMetadata };
				return this.parseJSONLText(pText, tmpSidecars, fCallback);
			}));

			return Promise.all(tmpReads).catch((pErr) => fCallback(pErr));
		}).catch((pErr) => fCallback(pErr));
	}

	parseJSONLText(pText, pSidecars, fCallback)
	{
		try
		{
			let tmpSidecars = pSidecars || {};
			let tmpRecords = this._parseJSONL(pText);
			let tmpSession = this._buildSession(tmpRecords, tmpSidecars);
			this.fable.AppData = this.fable.AppData || {};
			this.fable.AppData.Session = tmpSession;
			return fCallback(null, tmpSession);
		}
		catch (pError)
		{
			return fCallback(pError);
		}
	}

	// ---------------------------------------------------------------------
	// Building the session from raw records
	// ---------------------------------------------------------------------

	_parseJSONL(pText)
	{
		let tmpRecords = [];
		let tmpLines = pText.split(/\r?\n/);
		for (let i = 0; i < tmpLines.length; i++)
		{
			let tmpLine = tmpLines[i];
			if (!tmpLine || !tmpLine.trim()) continue;
			try
			{
				tmpRecords.push(JSON.parse(tmpLine));
			}
			catch (pError)
			{
				// Tolerate a malformed line — surface a placeholder so the user can see something went wrong.
				tmpRecords.push({ type: 'parse-error', error: pError.message, raw: tmpLine.slice(0, 300) });
			}
		}
		return tmpRecords;
	}

	_buildSession(pRecords, pSidecars)
	{
		let tmpToolResults = (pSidecars.ToolResults) || {};
		let tmpSubagents = (pSidecars.Subagents) || {};
		let tmpMetadata = pSidecars.Metadata || null;

		// Index assistant tool_use blocks so we can resolve durations when we see results.
		let tmpToolUseIndex = {};                             // toolUseId → { Name, StartedMs, AgentId|null }

		let tmpTurns = [];
		let tmpTitle = null;
		let tmpFirstMs = null;
		let tmpLastMs = null;

		for (let i = 0; i < pRecords.length; i++)
		{
			let tmpRec = pRecords[i];
			let tmpType = tmpRec.type;

			if (tmpType === 'ai-title' && tmpRec.aiTitle)
			{
				tmpTitle = tmpRec.aiTitle;
				continue;
			}
			if (tmpType === 'queue-operation' || tmpType === 'last-prompt' || tmpType === 'parse-error')
			{
				continue;
			}

			let tmpTimestampMs = this._parseTimestampMs(tmpRec.timestamp);
			if (tmpTimestampMs && (!tmpFirstMs || tmpTimestampMs < tmpFirstMs)) tmpFirstMs = tmpTimestampMs;
			if (tmpTimestampMs && (!tmpLastMs  || tmpTimestampMs > tmpLastMs )) tmpLastMs = tmpTimestampMs;

			if (tmpRec.isSidechain)
			{
				// Sidechain records also appear in subagent jsonl files; we render those separately.
				continue;
			}

			let tmpTurn = this._normalizeRecord(tmpRec, tmpToolUseIndex, tmpToolResults);
			if (tmpTurn) tmpTurns.push(tmpTurn);
		}

		// Sort by timestamp (records *should* already be ordered, but defensively).
		tmpTurns.sort((pA, pB) => (pA.TimestampMs || 0) - (pB.TimestampMs || 0));

		// Carry a "current promptId" forward to turns that don't carry one themselves.
		// Assistant records in Claude Code's JSONL carry NO promptId — only user records do — so
		// without this every assistant turn would be excluded from prompt grouping (and from the
		// final-reply / intermediary tagging that depends on the group).
		let tmpCurrentPromptId = null;
		for (let i = 0; i < tmpTurns.length; i++)
		{
			let tmpT = tmpTurns[i];
			if (tmpT.PromptId)
			{
				tmpCurrentPromptId = tmpT.PromptId;
			}
			else if (tmpCurrentPromptId)
			{
				tmpT.PromptId = tmpCurrentPromptId;
			}
		}

		// Compute gaps and inject gap turns where idle time is meaningful.
		// "Real" turns for gap purposes = anything that isn't background noise (attachment/system).
		// In real sessions, attachment + stop_hook_summary records get emitted between turns and
		// would otherwise mask the actual idle time between an assistant finishing and the user
		// replying. So we look past those when checking whether a gap is human-facing.
		let tmpWithGaps = [];
		let tmpPrevMs = null;
		let tmpPrevReal = null;
		for (let i = 0; i < tmpTurns.length; i++)
		{
			let tmpT = tmpTurns[i];
			if (tmpPrevMs && tmpT.TimestampMs)
			{
				let tmpDelta = tmpT.TimestampMs - tmpPrevMs;
				tmpT.DurationFromPrevMs = tmpDelta;
				if (tmpDelta >= _MinIdleGapMs && this._isHumanFacingGap(tmpPrevReal, tmpT))
				{
					tmpWithGaps.push({
						Kind: 'gap',
						TimestampMs: tmpPrevMs,
						EndTimestampMs: tmpT.TimestampMs,
						DurationMs: tmpDelta,
						Blocks: []
					});
				}
			}
			tmpWithGaps.push(tmpT);
			tmpPrevMs = tmpT.TimestampMs || tmpPrevMs;
			if (tmpT.Kind !== 'attachment' && tmpT.Kind !== 'system') tmpPrevReal = tmpT;
		}

		// Build prompt groups (everything caused by a single user prompt).
		let tmpPromptOrder = [];
		let tmpPromptMap = {};
		for (let i = 0; i < tmpWithGaps.length; i++)
		{
			let tmpT = tmpWithGaps[i];
			if (!tmpT.PromptId) continue;
			let tmpGroup = tmpPromptMap[tmpT.PromptId];
			if (!tmpGroup)
			{
				tmpGroup = {
					PromptId: tmpT.PromptId,
					Title: '',
					FirstTimestampMs: tmpT.TimestampMs,
					LastTimestampMs: tmpT.TimestampMs,
					ResponseLatencyMs: null,
					TotalDurationMs: 0,
					IdleAfterMs: 0,
					ToolCallCount: 0,
					AgentCallCount: 0,
					TurnIndices: []
				};
				tmpPromptMap[tmpT.PromptId] = tmpGroup;
				tmpPromptOrder.push(tmpT.PromptId);
			}
			tmpGroup.TurnIndices.push(i);
			if (tmpT.TimestampMs)
			{
				if (tmpT.TimestampMs < tmpGroup.FirstTimestampMs) tmpGroup.FirstTimestampMs = tmpT.TimestampMs;
				if (tmpT.TimestampMs > tmpGroup.LastTimestampMs)  tmpGroup.LastTimestampMs  = tmpT.TimestampMs;
			}
			if (tmpT.Kind === 'user-prompt' && !tmpGroup.Title)
			{
				let tmpUserText = this._extractPromptText(tmpT);
				if (tmpUserText)
				{
					tmpGroup.Title = this._summarizeText(tmpUserText, 90);
				}
				else if (tmpT.HarnessReminders && tmpT.HarnessReminders.length)
				{
					// Harness-only prompt — synthesize a title from the first reminder so it doesn't
					// render as a blank row in the timeline rail.
					tmpGroup.Title = '⚙ ' + this._summarizeText(tmpT.HarnessReminders[0].Body, 80);
					tmpGroup.IsHarnessOnly = true;
				}
			}
			if (tmpT.Kind === 'assistant')
			{
				tmpGroup.ToolCallCount += (tmpT.ToolUseIds || []).length;
				if (tmpGroup.ResponseLatencyMs == null)
				{
					// Latency = first assistant-turn timestamp − first user-prompt timestamp in this group.
					let tmpFirstUser = null;
					for (let j = 0; j < tmpGroup.TurnIndices.length; j++)
					{
						let tmpInner = tmpWithGaps[tmpGroup.TurnIndices[j]];
						if (tmpInner.Kind === 'user-prompt') { tmpFirstUser = tmpInner; break; }
					}
					if (tmpFirstUser && tmpFirstUser.TimestampMs && tmpT.TimestampMs)
					{
						tmpGroup.ResponseLatencyMs = tmpT.TimestampMs - tmpFirstUser.TimestampMs;
					}
				}
			}
			if (tmpT.AgentId) tmpGroup.AgentCallCount += 1;
		}
		// Finalize totals.
		for (let i = 0; i < tmpPromptOrder.length; i++)
		{
			let tmpG = tmpPromptMap[tmpPromptOrder[i]];
			tmpG.TotalDurationMs = (tmpG.LastTimestampMs && tmpG.FirstTimestampMs)
				? tmpG.LastTimestampMs - tmpG.FirstTimestampMs : 0;
		}

		// Tag final-reply vs intermediary on each assistant turn within a prompt group.
		// "Final reply" = the LAST assistant turn in the group that contains non-empty text.
		// "Intermediary" = any earlier assistant turn that also contains non-empty text (the chatter
		// the agent emits while working: "I'll start by exploring…", "Now let me read X.", etc.).
		// Assistant turns that are pure tool_use carriers (no text) are neither — they're tool plumbing.
		let tmpHasMeaningfulText = (pTurn) =>
		{
			if (!pTurn.Blocks) return false;
			for (let k = 0; k < pTurn.Blocks.length; k++)
			{
				let tmpBlk = pTurn.Blocks[k];
				if (tmpBlk.Type === 'text' && tmpBlk.Text && tmpBlk.Text.trim()) return true;
			}
			return false;
		};
		let tmpHasToolUse = (pTurn) =>
		{
			if (!pTurn.Blocks) return false;
			for (let k = 0; k < pTurn.Blocks.length; k++)
			{
				if (pTurn.Blocks[k].Type === 'tool_use') return true;
			}
			return false;
		};
		// Mark assistant turns that are pure tool plumbing (only tool_use blocks, no meaningful text).
		// These should hide along with the tools toggle, since they are not part of the conversation.
		// Also mark turns whose only payload is a `thinking` block (often an encrypted-signature-only
		// thinking record produced by extended-thinking models) — these would otherwise render as
		// empty cards because the inner thinking body is gated by the attachments toggle. Hiding the
		// wrapper under the same toggle keeps the conversation pane clean by default.
		let tmpHasThinking = (pTurn) =>
		{
			if (!pTurn.Blocks) return false;
			for (let k = 0; k < pTurn.Blocks.length; k++)
			{
				if (pTurn.Blocks[k].Type === 'thinking') return true;
			}
			return false;
		};
		for (let i = 0; i < tmpWithGaps.length; i++)
		{
			let tmpTurnRef = tmpWithGaps[i];
			if (tmpTurnRef.Kind !== 'assistant') continue;
			tmpTurnRef.IsToolOnly = !tmpHasMeaningfulText(tmpTurnRef) && tmpHasToolUse(tmpTurnRef);
			tmpTurnRef.IsThinkingOnly = !tmpHasMeaningfulText(tmpTurnRef) && !tmpHasToolUse(tmpTurnRef) && tmpHasThinking(tmpTurnRef);
			// Catch the truly empty case too (no text, no tool_use, no thinking) — e.g. a stop-only
			// assistant message — and treat it the same so it doesn't ghost the layout.
			if (!tmpHasMeaningfulText(tmpTurnRef) && !tmpHasToolUse(tmpTurnRef) && !tmpHasThinking(tmpTurnRef))
			{
				tmpTurnRef.IsThinkingOnly = true;
			}
		}
		for (let i = 0; i < tmpPromptOrder.length; i++)
		{
			let tmpG = tmpPromptMap[tmpPromptOrder[i]];
			let tmpFinalTurnIdx = -1;
			for (let j = tmpG.TurnIndices.length - 1; j >= 0; j--)
			{
				let tmpTurnRef = tmpWithGaps[tmpG.TurnIndices[j]];
				if (tmpTurnRef.Kind === 'assistant' && tmpHasMeaningfulText(tmpTurnRef))
				{
					tmpFinalTurnIdx = tmpG.TurnIndices[j];
					break;
				}
			}
			for (let j = 0; j < tmpG.TurnIndices.length; j++)
			{
				let tmpTurnRef = tmpWithGaps[tmpG.TurnIndices[j]];
				if (tmpTurnRef.Kind !== 'assistant') continue;
				if (tmpG.TurnIndices[j] === tmpFinalTurnIdx)
				{
					tmpTurnRef.IsFinalReply = true;
					tmpTurnRef.IsIntermediary = false;
				}
				else
				{
					tmpTurnRef.IsFinalReply = false;
					tmpTurnRef.IsIntermediary = tmpHasMeaningfulText(tmpTurnRef);
				}
			}
		}

		// Subagents → normalized agent transcripts.
		let tmpAgents = {};
		let tmpAgentIds = Object.keys(tmpSubagents);
		for (let i = 0; i < tmpAgentIds.length; i++)
		{
			let tmpAgentId = tmpAgentIds[i];
			let tmpRaw = tmpSubagents[tmpAgentId];
			tmpAgents[tmpAgentId] = this._buildAgentTranscript(tmpAgentId, tmpRaw, tmpToolResults);
		}

		// Stats.
		// Engaged-time accounting: walk turn-to-turn deltas and attribute each one to either Claude
		// (the model was generating, or a tool was running) or you (you were typing/reading the
		// previous reply). Idle gaps are already extracted as their own kind='gap' turns and counted
		// separately in tmpIdleMs.
		let tmpToolCount = 0;
		let tmpIdleMs = 0;
		let tmpLongestGap = 0;
		let tmpClaudeMs = 0;
		let tmpUserMs = 0;
		let tmpChatterCount = 0;          // IsIntermediary assistant turns
		let tmpAttachmentItemCount = 0;   // everything controlled by the attachments toggle
		let tmpPrevAttribMs = null;
		for (let i = 0; i < tmpWithGaps.length; i++)
		{
			let tmpT = tmpWithGaps[i];
			if (tmpT.Kind === 'gap')
			{
				let tmpDur = tmpT.DurationMs || 0;
				if (tmpDur >= _StandbyThresholdMs)
				{
					// Long pause — count as standby (AFK time).
					tmpIdleMs += tmpDur;
				}
				else
				{
					// Short pause between prompts — credit as you-working (reading + composing).
					tmpUserMs += tmpDur;
				}
				if (tmpDur > tmpLongestGap) tmpLongestGap = tmpDur;
				// Reset prev so the gap span doesn't get re-counted on the next delta.
				tmpPrevAttribMs = tmpT.EndTimestampMs || null;
				continue;
			}
			if (tmpT.Kind === 'assistant') tmpToolCount += (tmpT.ToolUseIds || []).length;

			// Visibility-toggle counts: how many things each toggle would hide.
			if (tmpT.Kind === 'assistant' && tmpT.IsIntermediary) tmpChatterCount++;
			if (tmpT.Kind === 'attachment' || tmpT.Kind === 'system') tmpAttachmentItemCount++;
			if (tmpT.Kind === 'assistant' && tmpT.IsThinkingOnly)  tmpAttachmentItemCount++;
			if (tmpT.Kind === 'user-prompt' && tmpT.IsHarnessOnly) tmpAttachmentItemCount++;
			// Inline thinking blocks inside otherwise-visible assistant turns.
			if (tmpT.Kind === 'assistant' && !tmpT.IsThinkingOnly && tmpT.Blocks)
			{
				for (let b = 0; b < tmpT.Blocks.length; b++)
				{
					if (tmpT.Blocks[b].Type === 'thinking') tmpAttachmentItemCount++;
				}
			}
			// Inline harness reminders embedded in user prompts that ALSO have user text.
			if (tmpT.Kind === 'user-prompt' && !tmpT.IsHarnessOnly && tmpT.HarnessReminders)
			{
				tmpAttachmentItemCount += tmpT.HarnessReminders.length;
			}

			if (tmpPrevAttribMs && tmpT.TimestampMs)
			{
				let tmpDelta = tmpT.TimestampMs - tmpPrevAttribMs;
				if (tmpDelta > 0)
				{
					// Time leading INTO this turn belongs to whichever side caused it.
					// Assistant turns and tool-result turns are Claude-side (Claude generated,
					// or Claude orchestrated a tool that ran). User-prompts are you-side (you
					// were composing the next message). Attachments / system records are noise —
					// don't credit either side for them.
					if (tmpT.Kind === 'assistant' || tmpT.Kind === 'tool-result')
					{
						tmpClaudeMs += tmpDelta;
					}
					else if (tmpT.Kind === 'user-prompt')
					{
						tmpUserMs += tmpDelta;
					}
				}
			}
			if (tmpT.TimestampMs) tmpPrevAttribMs = tmpT.TimestampMs;
		}
		let tmpTotalDurationMs = (tmpFirstMs && tmpLastMs) ? tmpLastMs - tmpFirstMs : 0;
		let tmpEngagedMs = Math.max(0, tmpTotalDurationMs - tmpIdleMs);

		let tmpMeta = Object.assign(
			{
				title: tmpTitle || (tmpMetadata && tmpMetadata.title) || 'Untitled session',
				sessionId: tmpMetadata && tmpMetadata.cliSessionId,
				createdAt: tmpFirstMs,
				lastActivityAt: tmpLastMs
			},
			tmpMetadata || {}
		);

		return {
			Meta: tmpMeta,
			Turns: tmpWithGaps,
			Prompts: tmpPromptOrder.map((pId) => tmpPromptMap[pId]),
			Agents: tmpAgents,
			Stats:
			{
				totalDurationMs: tmpTotalDurationMs,
				idleMs: tmpIdleMs,                     // legacy alias for standby
				standbyMs: tmpIdleMs,
				engagedMs: tmpEngagedMs,               // total - standby
				activeMs: tmpEngagedMs,                // legacy alias
				claudeWorkingMs: tmpClaudeMs,
				userWorkingMs: tmpUserMs,
				toolCallCount: tmpToolCount,
				promptCount: tmpPromptOrder.length,
				agentCount: Object.keys(tmpAgents).length,
				longestGapMs: tmpLongestGap,
				chatterCount: tmpChatterCount,
				attachmentItemCount: tmpAttachmentItemCount
			}
		};
	}

	// ---------------------------------------------------------------------
	// Per-record normalization
	// ---------------------------------------------------------------------

	_normalizeRecord(pRec, pToolUseIndex, pToolResults)
	{
		let tmpType = pRec.type;
		let tmpTimestampMs = this._parseTimestampMs(pRec.timestamp);
		let tmpBase =
		{
			Uuid:        pRec.uuid || null,
			ParentUuid:  pRec.parentUuid || null,
			PromptId:    pRec.promptId || null,
			Timestamp:   pRec.timestamp || null,
			TimestampMs: tmpTimestampMs,
			DurationFromPrevMs: 0,
			Blocks:      [],
			ToolUseIds:  [],
			AgentId:     null,
			Raw:         null
		};

		if (tmpType === 'attachment')
		{
			let tmpAttach = pRec.attachment || {};
			tmpBase.Kind = 'attachment';
			tmpBase.Blocks.push({
				Type: 'attachment',
				AttachmentType: tmpAttach.type || 'unknown',
				Summary: this._summarizeAttachment(tmpAttach),
				Detail: tmpAttach
			});
			return tmpBase;
		}

		if (tmpType === 'system')
		{
			tmpBase.Kind = 'system';
			tmpBase.Blocks.push({
				Type: 'system',
				Subtype: pRec.subtype || 'system',
				Summary: this._summarizeSystem(pRec)
			});
			return tmpBase;
		}

		if (tmpType === 'user')
		{
			let tmpContent = pRec.message && pRec.message.content;
			// Pure text user prompt
			if (typeof tmpContent === 'string')
			{
				tmpBase.Kind = 'user-prompt';
				let tmpExtracted = this._extractHarnessTags(tmpContent);
				if (tmpExtracted.UserText)
				{
					tmpBase.Blocks.push({ Type: 'text', Text: tmpExtracted.UserText });
				}
				if (tmpExtracted.Reminders.length)
				{
					tmpBase.HarnessReminders = tmpExtracted.Reminders;
				}
				tmpBase.IsHarnessOnly = !tmpExtracted.UserText && tmpExtracted.Reminders.length > 0;
				return tmpBase;
			}
			// Array form: tool_results, image, etc.
			if (Array.isArray(tmpContent))
			{
				let tmpHasToolResult = false;
				let tmpHasUserText = false;
				for (let i = 0; i < tmpContent.length; i++)
				{
					let tmpBlock = tmpContent[i];
					if (!tmpBlock || typeof tmpBlock !== 'object') continue;
					if (tmpBlock.type === 'tool_result')
					{
						tmpHasToolResult = true;
						let tmpResolved = this._resolveToolResultContent(tmpBlock, pToolResults);
						let tmpToolUseId = tmpBlock.tool_use_id;
						let tmpToolMeta = pToolUseIndex[tmpToolUseId] || {};
						let tmpDurationMs = (tmpToolMeta.StartedMs && tmpTimestampMs)
							? tmpTimestampMs - tmpToolMeta.StartedMs : null;
						tmpBase.Blocks.push({
							Type: 'tool_result',
							ToolUseId: tmpToolUseId,
							ToolName: tmpToolMeta.Name || null,
							IsError: !!tmpBlock.is_error,
							DurationMs: tmpDurationMs,
							ContentText: tmpResolved.Text,
							ContentTruncated: tmpResolved.Truncated,
							PersistedPath: tmpResolved.PersistedPath,
							ToolUseResult: pRec.toolUseResult || null
						});
					}
					else if (tmpBlock.type === 'text')
					{
						let tmpExtracted = this._extractHarnessTags(tmpBlock.text || '');
						if (tmpExtracted.UserText)
						{
							tmpHasUserText = true;
							tmpBase.Blocks.push({ Type: 'text', Text: tmpExtracted.UserText });
						}
						if (tmpExtracted.Reminders.length)
						{
							tmpBase.HarnessReminders = (tmpBase.HarnessReminders || []).concat(tmpExtracted.Reminders);
						}
					}
					else if (tmpBlock.type === 'image')
					{
						tmpBase.Blocks.push({ Type: 'image', Source: tmpBlock.source || null });
					}
					else
					{
						tmpBase.Blocks.push({ Type: tmpBlock.type || 'unknown', Raw: tmpBlock });
					}
				}
				if (tmpHasToolResult && !tmpHasUserText) tmpBase.Kind = 'tool-result';
				else tmpBase.Kind = 'user-prompt';
				return tmpBase;
			}
			return null;
		}

		if (tmpType === 'assistant')
		{
			tmpBase.Kind = 'assistant';
			let tmpMsg = pRec.message || {};
			let tmpUsage = tmpMsg.usage || null;
			let tmpContent = tmpMsg.content || [];
			for (let i = 0; i < tmpContent.length; i++)
			{
				let tmpBlock = tmpContent[i];
				if (!tmpBlock || typeof tmpBlock !== 'object') continue;
				if (tmpBlock.type === 'text')
				{
					tmpBase.Blocks.push({ Type: 'text', Text: tmpBlock.text || '' });
				}
				else if (tmpBlock.type === 'thinking')
				{
					tmpBase.Blocks.push({ Type: 'thinking', Text: tmpBlock.thinking || '' });
				}
				else if (tmpBlock.type === 'tool_use')
				{
					let tmpToolUseId = tmpBlock.id;
					let tmpAgentId = (tmpBlock.input && (tmpBlock.input.subagent_type || tmpBlock.input.agentId)) || null;
					pToolUseIndex[tmpToolUseId] =
					{
						Name: tmpBlock.name,
						StartedMs: tmpTimestampMs,
						AgentId: tmpAgentId
					};
					tmpBase.ToolUseIds.push(tmpToolUseId);
					tmpBase.Blocks.push({
						Type: 'tool_use',
						ToolUseId: tmpToolUseId,
						Name: tmpBlock.name,
						Input: tmpBlock.input || {},
						InputSummary: this._summarizeToolInput(tmpBlock.name, tmpBlock.input)
					});
				}
				else
				{
					tmpBase.Blocks.push({ Type: tmpBlock.type || 'unknown', Raw: tmpBlock });
				}
			}
			tmpBase.Usage = tmpUsage ? {
				input_tokens: tmpUsage.input_tokens,
				output_tokens: tmpUsage.output_tokens,
				cache_read: tmpUsage.cache_read_input_tokens,
				cache_create: tmpUsage.cache_creation_input_tokens
			} : null;
			tmpBase.Model = tmpMsg.model || null;
			tmpBase.StopReason = tmpMsg.stop_reason || null;
			return tmpBase;
		}

		return null;
	}

	// ---------------------------------------------------------------------
	// Subagent transcripts (same shape as main, minus prompt grouping)
	// ---------------------------------------------------------------------

	_buildAgentTranscript(pAgentId, pRaw, pToolResults)
	{
		let tmpRecords = pRaw && pRaw.Text ? this._parseJSONL(pRaw.Text) : [];
		let tmpToolUseIndex = {};
		let tmpTurns = [];
		let tmpFirstMs = null;
		let tmpLastMs = null;

		for (let i = 0; i < tmpRecords.length; i++)
		{
			let tmpRec = tmpRecords[i];
			let tmpTimestampMs = this._parseTimestampMs(tmpRec.timestamp);
			if (tmpTimestampMs && (!tmpFirstMs || tmpTimestampMs < tmpFirstMs)) tmpFirstMs = tmpTimestampMs;
			if (tmpTimestampMs && (!tmpLastMs  || tmpTimestampMs > tmpLastMs )) tmpLastMs = tmpTimestampMs;
			let tmpTurn = this._normalizeRecord(tmpRec, tmpToolUseIndex, pToolResults);
			if (tmpTurn) tmpTurns.push(tmpTurn);
		}
		tmpTurns.sort((pA, pB) => (pA.TimestampMs || 0) - (pB.TimestampMs || 0));

		// Compute prev-deltas for the agent timeline too.
		let tmpPrev = null;
		for (let i = 0; i < tmpTurns.length; i++)
		{
			if (tmpPrev && tmpTurns[i].TimestampMs)
			{
				tmpTurns[i].DurationFromPrevMs = tmpTurns[i].TimestampMs - tmpPrev;
			}
			tmpPrev = tmpTurns[i].TimestampMs || tmpPrev;
		}

		let tmpToolCount = 0;
		for (let i = 0; i < tmpTurns.length; i++)
		{
			if (tmpTurns[i].Kind === 'assistant') tmpToolCount += (tmpTurns[i].ToolUseIds || []).length;
		}

		return {
			AgentId: pAgentId,
			AgentType: pRaw && pRaw.Meta && pRaw.Meta.agentType || 'agent',
			Description: pRaw && pRaw.Meta && pRaw.Meta.description || '',
			FirstTimestampMs: tmpFirstMs,
			LastTimestampMs: tmpLastMs,
			DurationMs: (tmpFirstMs && tmpLastMs) ? tmpLastMs - tmpFirstMs : 0,
			ToolCallCount: tmpToolCount,
			Turns: tmpTurns
		};
	}

	// ---------------------------------------------------------------------
	// Helpers
	// ---------------------------------------------------------------------

	_isHumanFacingGap(pPrev, pNext)
	{
		// Surface gaps that represent the user thinking/walking away — i.e. assistant finished and user
		// is the one taking time to respond (or the reverse). Tool round-trips inside an assistant turn
		// are noise.
		if (!pPrev || !pNext) return false;
		if (pPrev.Kind === 'assistant' && pNext.Kind === 'user-prompt') return true;
		if (pPrev.Kind === 'tool-result' && pNext.Kind === 'user-prompt') return true;
		if (pPrev.Kind === 'user-prompt' && pNext.Kind === 'assistant') return true;
		return false;
	}

	_resolveToolResultContent(pBlock, pToolResults)
	{
		let tmpRaw = '';
		let tmpContent = pBlock.content;
		if (typeof tmpContent === 'string') tmpRaw = tmpContent;
		else if (Array.isArray(tmpContent))
		{
			for (let i = 0; i < tmpContent.length; i++)
			{
				let tmpC = tmpContent[i];
				if (tmpC && tmpC.type === 'text') tmpRaw += tmpC.text || '';
				else if (typeof tmpC === 'string') tmpRaw += tmpC;
			}
		}
		// Spilled content?  <persisted-output>... tool-results/<key>.txt ...
		let tmpMatch = tmpRaw.match(/tool-results\/([^/\s"]+)\.txt/);
		let tmpPersistedPath = null;
		if (tmpMatch)
		{
			let tmpKey = tmpMatch[1] + '.txt';
			tmpPersistedPath = tmpKey;
			let tmpFull = pToolResults[tmpKey];
			if (tmpFull) tmpRaw = tmpFull;
		}
		let tmpTruncated = false;
		if (tmpRaw.length > _MaxInlineToolResultPreview)
		{
			tmpRaw = tmpRaw.slice(0, _MaxInlineToolResultPreview);
			tmpTruncated = true;
		}
		return { Text: tmpRaw, Truncated: tmpTruncated, PersistedPath: tmpPersistedPath };
	}

	_summarizeToolInput(pName, pInput)
	{
		if (!pInput || typeof pInput !== 'object') return '';
		switch (pName)
		{
			case 'Read':       return pInput.file_path || '';
			case 'Write':      return pInput.file_path || '';
			case 'Edit':       return pInput.file_path || '';
			case 'Bash':       return (pInput.description ? pInput.description + ' — ' : '') + (pInput.command || '').slice(0, 120);
			case 'Glob':       return pInput.pattern || '';
			case 'Grep':       return (pInput.pattern || '') + (pInput.path ? ' in ' + pInput.path : '');
			case 'WebFetch':   return pInput.url || '';
			case 'WebSearch':  return pInput.query || '';
			case 'Task':
			case 'Agent':      return (pInput.subagent_type ? '[' + pInput.subagent_type + '] ' : '') + (pInput.description || '');
			case 'TodoWrite':  return (pInput.todos && pInput.todos.length) ? pInput.todos.length + ' todos' : '';
			case 'Skill':      return pInput.skill || '';
		}
		// Generic fallback: shortest scalar field.
		let tmpKeys = Object.keys(pInput);
		for (let i = 0; i < tmpKeys.length; i++)
		{
			let tmpVal = pInput[tmpKeys[i]];
			if (typeof tmpVal === 'string' && tmpVal.length < 200) return tmpKeys[i] + '=' + tmpVal;
		}
		return '';
	}

	_summarizeAttachment(pAttach)
	{
		switch (pAttach.type)
		{
			case 'deferred_tools_delta':
				return (pAttach.addedNames ? pAttach.addedNames.length + ' deferred tools added' : 'deferred tools change');
			case 'skill_listing':
				return 'skill listing';
			default:
				return pAttach.type || 'attachment';
		}
	}

	_summarizeSystem(pRec)
	{
		if (pRec.subtype === 'stop_hook_summary')
		{
			return 'stop hook (' + (pRec.hookCount || 0) + ' hooks)';
		}
		return pRec.subtype || 'system';
	}

	_extractPromptText(pTurn)
	{
		for (let i = 0; i < pTurn.Blocks.length; i++)
		{
			let tmpB = pTurn.Blocks[i];
			if (tmpB.Type === 'text' && tmpB.Text) return tmpB.Text;
		}
		return '';
	}

	_extractHarnessTags(pText)
	{
		// Strip out tags the harness injects into user-message content (system reminders,
		// slash-command markup) so the conversation view can render the user's actual words and
		// surface the injected text as an attachment-style annotation. There's no separate field
		// in the JSONL — the tags are concatenated into message.content as plain text.
		if (!pText || typeof pText !== 'string') return { UserText: '', Reminders: [] };
		let tmpReminders = [];
		let tmpTagRegex = /<(system-reminder|command-message|command-name|command-args)>([\s\S]*?)<\/\1>/g;
		let tmpClean = pText.replace(tmpTagRegex, function (pMatch, pTag, pBody)
		{
			tmpReminders.push({ Tag: pTag, Body: (pBody || '').trim() });
			return '';
		}).trim();
		return { UserText: tmpClean, Reminders: tmpReminders };
	}

	_summarizeText(pText, pMaxLen)
	{
		if (!pText) return '';
		let tmpClean = pText.replace(/\s+/g, ' ').trim();
		if (tmpClean.length <= pMaxLen) return tmpClean;
		return tmpClean.slice(0, pMaxLen - 1) + '…';
	}

	_parseTimestampMs(pIso)
	{
		if (!pIso) return null;
		let tmpT = Date.parse(pIso);
		return isNaN(tmpT) ? null : tmpT;
	}

	// ---------------------------------------------------------------------
	// Browser plumbing (dropped folder / FileReader / JSZip)
	// ---------------------------------------------------------------------

	_loadJSZip()
	{
		if (this._JSZip) return this._JSZip;
		if (typeof window !== 'undefined' && window.JSZip) { this._JSZip = window.JSZip; return this._JSZip; }
		try { this._JSZip = require('jszip'); return this._JSZip; }
		catch (pErr) { return null; }
	}

	_readFileAsArrayBuffer(pFile, fCallback)
	{
		try
		{
			let tmpReader = new FileReader();
			tmpReader.onload = () => fCallback(null, tmpReader.result);
			tmpReader.onerror = () => fCallback(tmpReader.error || new Error('Read failed.'));
			tmpReader.readAsArrayBuffer(pFile);
		}
		catch (pErr) { fCallback(pErr); }
	}

	_readFileAsText(pFile, fCallback)
	{
		try
		{
			let tmpReader = new FileReader();
			tmpReader.onload = () => fCallback(null, tmpReader.result);
			tmpReader.onerror = () => fCallback(tmpReader.error || new Error('Read failed.'));
			tmpReader.readAsText(pFile);
		}
		catch (pErr) { fCallback(pErr); }
	}

	_parseDroppedFolder(pFiles, fCallback)
	{
		let tmpJsonl = null;
		let tmpToolResults = {};
		let tmpSubagents = {};
		let tmpMetadata = null;
		let tmpPending = 0;
		let tmpDone = false;

		let tmpFinish = (pErr, pSession) =>
		{
			if (tmpDone) return;
			tmpDone = true;
			if (pErr) return fCallback(pErr);
			fCallback(null, pSession);
		};

		let tmpKick = () =>
		{
			if (tmpPending > 0) return;
			if (!tmpJsonl) return tmpFinish(new Error('No .jsonl found in dropped files.'));
			let tmpSidecars = { ToolResults: tmpToolResults, Subagents: tmpSubagents, Metadata: tmpMetadata };
			this.parseJSONLText(tmpJsonl, tmpSidecars, tmpFinish);
		};

		for (let i = 0; i < pFiles.length; i++)
		{
			let tmpFile = pFiles[i];
			let tmpPath = tmpFile.webkitRelativePath || tmpFile.name;

			if (/\.jsonl$/i.test(tmpPath) && tmpPath.indexOf('/subagents/') === -1)
			{
				tmpPending++;
				this._readFileAsText(tmpFile, (pErr, pText) =>
				{
					if (!pErr) tmpJsonl = pText;
					tmpPending--; tmpKick();
				});
			}
			else if (/\/metadata\.json$/i.test(tmpPath) || /^metadata\.json$/i.test(tmpPath))
			{
				tmpPending++;
				this._readFileAsText(tmpFile, (pErr, pText) =>
				{
					if (!pErr) { try { tmpMetadata = JSON.parse(pText); } catch(e){} }
					tmpPending--; tmpKick();
				});
			}
			else if (tmpPath.indexOf('/tool-results/') !== -1)
			{
				let tmpKey = tmpPath.split('/').pop();
				tmpPending++;
				this._readFileAsText(tmpFile, (pErr, pText) =>
				{
					if (!pErr) tmpToolResults[tmpKey] = pText;
					tmpPending--; tmpKick();
				});
			}
			else
			{
				let tmpAgentMatch = tmpPath.match(/\/subagents\/agent-([^./]+)(\.meta\.json|\.jsonl)$/i);
				if (tmpAgentMatch)
				{
					let tmpAgentId = tmpAgentMatch[1];
					let tmpKind = tmpAgentMatch[2];
					tmpSubagents[tmpAgentId] = tmpSubagents[tmpAgentId] || { Meta: null, Text: '' };
					tmpPending++;
					this._readFileAsText(tmpFile, (pErr, pText) =>
					{
						if (!pErr)
						{
							if (tmpKind === '.meta.json') { try { tmpSubagents[tmpAgentId].Meta = JSON.parse(pText); } catch(e){} }
							else { tmpSubagents[tmpAgentId].Text = pText; }
						}
						tmpPending--; tmpKick();
					});
				}
			}
		}
		// In case nothing kicked off async work, finish immediately with an error.
		if (tmpPending === 0) tmpKick();
	}
}

module.exports = PictProviderSessionParser;
module.exports.default_configuration = _DefaultProviderConfiguration;
